import { useCallback, useEffect, useState } from 'react';
import { lookupSession, type Session } from '../api/mediatorService';
import type { Case } from '../types/case';

type State =
  | { status: 'loading' }
  | { status: 'unlinked' }
  | { status: 'ready'; sessionId: string; session: Session; case: Case }
  | { status: 'error'; error: string };

export interface CaseApi {
  state: State;
  /** Updates just the session in state (used after lifecycle mutations). */
  updateSession: (session: Session) => void;
}

/**
 * Loads the Session + Case bound to the current Zoom meeting. When the
 * mediator opens the app in a meeting that has no LevelHeaded session yet,
 * the mediator service responds 404 and we transition to `unlinked` so the
 * UI can render the empty state.
 */
export function useCase(meetingId: string | null, accessToken: string | null): CaseApi {
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    if (!meetingId || !accessToken) return;

    let cancelled = false;
    setState({ status: 'loading' });

    lookupSession(meetingId, accessToken)
      .then((result) => {
        if (cancelled) return;
        if (!result) {
          setState({ status: 'unlinked' });
          return;
        }
        setState({
          status: 'ready',
          sessionId: result.session_id,
          session: result.session,
          case: result.case,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          error: err instanceof Error ? err.message : 'Failed to load case',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [meetingId, accessToken]);

  const updateSession = useCallback((session: Session) => {
    setState((prev) => (prev.status === 'ready' ? { ...prev, session } : prev));
  }, []);

  return { state, updateSession };
}
